import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  StyleSheet,
  ScrollView,
  Text,
  TouchableOpacity,
  View as RNView,
  Modal,
  Pressable,
  ActivityIndicator,
  TextInput,
  Switch,
  Image,
  Platform,
  KeyboardAvoidingView,
  useWindowDimensions,
  NativeSyntheticEvent,
  NativeScrollEvent,
  LayoutChangeEvent,
} from "react-native";
import FeatherIcon from "react-native-vector-icons/Feather";
import { useSelector } from "react-redux";
import { useAppTheme } from "../../../../hooks/useAppTheme";
import { ThemeColors } from "../../../../utils/constants/colors";

// ─── Design Tokens (shared) ───────────────────────────────────────────────────

const getC = (colors: ThemeColors, isDark: boolean) => ({
  navy: isDark ? colors.surface : "#0F1B2D",
  accent: colors.primary,
  success: colors.success,
  successBg: isDark ? "rgba(14, 159, 110, 0.2)" : "#EDFAF4",
  warning: colors.warning,
  warningBg: isDark ? "rgba(245, 158, 11, 0.2)" : "#FFF8EC",
  danger: colors.danger,
  dangerBg: isDark ? "rgba(231, 76, 60, 0.2)" : "#FEF0EE",
  surface: colors.surface,
  surfaceAlt: isDark ? colors.surfaceSubtle : "#F5F7FB",
  border: colors.borderSubtle,
  borderLight: colors.borderSubtle,
  text: colors.text,
  textMid: colors.textSecondary,
  textMuted: colors.textMuted,
  overlay: "rgba(10,20,35,0.5)",
});

type Tokens = ReturnType<typeof getC>;

const useStyles = () => {
  const { colors, isDark } = useAppTheme();
  return useMemo(() => {
    const C = getC(colors, isDark);
    return { C, S: createStyles(C) };
  }, [colors, isDark]);
};

const webNoOutline =
  Platform.OS === "web" ? ({ outlineStyle: "none" } as any) : null;

// ─── Types ────────────────────────────────────────────────────────────────────

interface ContactRow {
  address: string;
  email: string;
  phoneNumber: string;
}
interface SalesRow {
  name: string;
  email: string;
}

export interface OrgFormData {
  name: string;
  description: string;
  cardCode: string; // comma-separated in the form
  prefix: string;
  logo: string;
  domains: string[];
  contacts: ContactRow[];
  sales: SalesRow[];
  states: string[];
  pricingEnabled: boolean;
  pricingModel: "default" | "sales";
  pricingSalesEmail: string | null;
  showAvailableStock: boolean;
  showOnOrderStock: boolean;
  showStockOnTransit: boolean;
  showGrowers: boolean;
}

/** What onSave receives: card codes go back as an array when the org uses one. */
export type OrgSavePayload = Omit<OrgFormData, "cardCode"> & {
  cardCode: string | string[];
};

export interface OrganizationInput {
  _id?: string;
  name?: string;
  description?: string;
  cardCode?: string | string[];
  prefix?: string;
  logo?: string;
  logoUrl?: string;
  domains?: string[];
  contacts?: ContactRow[];
  sales?: SalesRow[];
  states?: string[];
  pricingEnabled?: boolean;
  pricingModel?: string;
  pricingSalesEmail?: string | null;
  showAvailableStock?: boolean;
  showOnOrderStock?: boolean;
  showStockOnTransit?: boolean;
  showGrowers?: boolean;
}

interface AddEditOrganizationModalProps {
  visible: boolean;
  onClose: () => void;
  organization?: OrganizationInput | null;
  /** Throw to keep the dialog open; the error message is shown in the footer. */
  onSave: (data: OrgSavePayload) => Promise<void>;
}

type SectionKey =
  | "basic"
  | "domains"
  | "contacts"
  | "sales"
  | "pricing"
  | "states"
  | "visibility";

const SECTIONS: { key: SectionKey; label: string; icon: string }[] = [
  { key: "basic", label: "General", icon: "briefcase" },
  { key: "domains", label: "Domains", icon: "globe" },
  { key: "contacts", label: "Contacts", icon: "phone" },
  { key: "sales", label: "Sales reps", icon: "users" },
  { key: "pricing", label: "Pricing", icon: "dollar-sign" },
  { key: "states", label: "States", icon: "map-pin" },
  { key: "visibility", label: "Visibility", icon: "eye" },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const buildDefault = (org?: OrganizationInput | null): OrgFormData => ({
  name: org?.name ?? "",
  description: org?.description ?? "",
  cardCode: ([] as string[]).concat(org?.cardCode ?? []).join(", "),
  prefix: org?.prefix ?? "",
  logo: org?.logo ?? "",
  domains: org?.domains ?? [],
  contacts: org?.contacts ?? [],
  sales: org?.sales ?? [],
  states: org?.states ?? [],
  pricingEnabled: org?.pricingEnabled ?? false,
  pricingModel: org?.pricingModel === "sales" ? "sales" : "default",
  pricingSalesEmail: org?.pricingSalesEmail ?? null,
  showAvailableStock: org?.showAvailableStock ?? true,
  showOnOrderStock: org?.showOnOrderStock ?? true,
  showStockOnTransit: org?.showStockOnTransit ?? true,
  showGrowers: org?.showGrowers ?? true,
});

const toPayload = (
  form: OrgFormData,
  org?: OrganizationInput | null,
): OrgSavePayload => {
  const codes = form.cardCode
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  return {
    ...form,
    name: form.name.trim(),
    prefix: form.prefix.trim(),
    domains: form.domains.map((d) => d.trim()).filter(Boolean),
    cardCode:
      Array.isArray(org?.cardCode) || codes.length > 1
        ? codes
        : (codes[0] ?? ""),
  };
};

const validate = (form: OrgFormData): Record<string, string> => {
  const errs: Record<string, string> = {};
  if (!form.name.trim()) errs.name = "Organisation name is required";
  if (!form.cardCode.trim()) errs.cardCode = "Card code is required";
  if (!form.prefix.trim()) errs.prefix = "Order prefix is required";
  else if (form.prefix.trim().length > 5) errs.prefix = "Max 5 characters";
  if (form.pricingModel === "sales" && !form.pricingSalesEmail)
    errs.pricingSalesEmail = "Select a sales rep";
  if (form.states.length === 0) errs.states = "Select at least one state";
  form.contacts.forEach((c, i) => {
    if (!c.address.trim()) errs[`contact_${i}_address`] = "Required";
    if (!c.email.trim()) errs[`contact_${i}_email`] = "Required";
    else if (!EMAIL_RE.test(c.email.trim()))
      errs[`contact_${i}_email`] = "Enter a valid email";
    if (!c.phoneNumber.trim()) errs[`contact_${i}_phoneNumber`] = "Required";
  });
  form.sales.forEach((s, i) => {
    if (!s.name.trim()) errs[`sales_${i}_name`] = "Required";
    if (!s.email.trim()) errs[`sales_${i}_email`] = "Required";
    else if (!EMAIL_RE.test(s.email.trim()))
      errs[`sales_${i}_email`] = "Enter a valid email";
  });
  return errs;
};

const sectionForError = (key: string): SectionKey => {
  if (key.startsWith("contact_")) return "contacts";
  if (key.startsWith("sales_")) return "sales";
  if (key === "pricingSalesEmail") return "pricing";
  if (key === "states") return "states";
  return "basic";
};

const getInitials = (name: string) =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("");

// ─── Form primitives ──────────────────────────────────────────────────────────

const Section = ({
  title,
  description,
  icon,
  action,
  onLayout,
  last,
  children,
}: {
  title: string;
  description?: string;
  icon: string;
  action?: React.ReactNode;
  onLayout?: (e: LayoutChangeEvent) => void;
  last?: boolean;
  children: React.ReactNode;
}) => {
  const { C, S } = useStyles();
  return (
    <RNView style={[S.section, last && S.sectionLast]} onLayout={onLayout}>
      <RNView style={S.sectionHead}>
        <RNView style={S.sectionIcon}>
          <FeatherIcon name={icon} size={15} color={C.accent} />
        </RNView>
        <RNView style={{ flex: 1, minWidth: 0 }}>
          <Text style={S.sectionTitle}>{title}</Text>
          {!!description && <Text style={S.sectionDesc}>{description}</Text>}
        </RNView>
        {action}
      </RNView>
      {children}
    </RNView>
  );
};

const Field = ({
  label,
  required,
  error,
  hint,
  children,
}: {
  label?: string;
  required?: boolean;
  error?: string;
  hint?: string;
  children: React.ReactNode;
}) => {
  const { C, S } = useStyles();
  return (
    <RNView style={S.field}>
      {!!label && (
        <Text style={S.label}>
          {label}
          {required ? <Text style={{ color: C.danger }}> *</Text> : null}
        </Text>
      )}
      {children}
      {error ? (
        <Text style={S.errorText}>{error}</Text>
      ) : hint ? (
        <Text style={S.hintText}>{hint}</Text>
      ) : null}
    </RNView>
  );
};

const Input = ({
  value,
  onChangeText,
  placeholder,
  multiline,
  lines = 3,
  error,
  keyboardType,
  autoCapitalize,
  maxLength,
  icon,
}: {
  value: string;
  onChangeText: (v: string) => void;
  placeholder?: string;
  multiline?: boolean;
  lines?: number;
  error?: string;
  keyboardType?: any;
  autoCapitalize?: "none" | "sentences" | "words" | "characters";
  maxLength?: number;
  icon?: string;
}) => {
  const { C, S } = useStyles();
  const [focused, setFocused] = useState(false);
  return (
    <RNView
      style={[
        S.inputWrap,
        focused && S.inputWrapFocused,
        !!error && S.inputWrapError,
        multiline && {
          height: lines * 22 + 22,
          alignItems: "flex-start",
          paddingVertical: 10,
        },
      ]}
    >
      {!!icon && <FeatherIcon name={icon} size={14} color={C.textMuted} />}
      <TextInput
        style={[
          S.input,
          webNoOutline,
          multiline && { height: "100%", textAlignVertical: "top" },
        ]}
        value={value}
        onChangeText={onChangeText}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        placeholder={placeholder}
        placeholderTextColor={C.textMuted}
        multiline={multiline}
        numberOfLines={multiline ? lines : 1}
        keyboardType={keyboardType ?? "default"}
        autoCapitalize={autoCapitalize}
        maxLength={maxLength}
      />
    </RNView>
  );
};

/** Side-by-side on wide screens, stacked on narrow ones. */
const Cols = ({
  stack,
  children,
}: {
  stack: boolean;
  children: React.ReactNode;
}) => (
  <RNView style={stack ? { gap: 14 } : { flexDirection: "row", gap: 16 }}>
    {React.Children.map(children, (child) =>
      child ? (
        <RNView style={stack ? undefined : { flex: 1, minWidth: 0 }}>
          {child}
        </RNView>
      ) : null,
    )}
  </RNView>
);

const SmallButton = ({
  label,
  icon,
  onPress,
  tone = "accent",
}: {
  label: string;
  icon?: string;
  onPress: () => void;
  tone?: "accent" | "neutral";
}) => {
  const { C, S } = useStyles();
  const fg = tone === "accent" ? C.accent : C.textMid;
  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      style={(s: any) => [
        S.smallBtn,
        tone === "neutral" && S.smallBtnNeutral,
        (s.hovered || s.pressed) && { opacity: 0.8 },
      ]}
    >
      {!!icon && <FeatherIcon name={icon} size={13} color={fg} />}
      <Text style={[S.smallBtnText, { color: fg }]}>{label}</Text>
    </Pressable>
  );
};

const IconButton = ({
  icon,
  onPress,
  label,
  danger,
}: {
  icon: string;
  onPress: () => void;
  label: string;
  danger?: boolean;
}) => {
  const { C } = useStyles();
  return (
    <Pressable
      onPress={onPress}
      accessibilityLabel={label}
      hitSlop={6}
      style={(s: any) => ({
        width: 36,
        height: 36,
        borderRadius: 8,
        justifyContent: "center",
        alignItems: "center",
        backgroundColor:
          s.hovered || s.pressed
            ? danger
              ? C.dangerBg
              : C.surfaceAlt
            : "transparent",
      })}
    >
      <FeatherIcon
        name={icon}
        size={15}
        color={danger ? C.danger : C.textMuted}
      />
    </Pressable>
  );
};

const EmptyBox = ({
  text,
  actionLabel,
  onAction,
}: {
  text: string;
  actionLabel: string;
  onAction: () => void;
}) => {
  const { S } = useStyles();
  return (
    <RNView style={S.emptyBox}>
      <Text style={S.emptyText}>{text}</Text>
      <SmallButton label={actionLabel} icon="plus" onPress={onAction} />
    </RNView>
  );
};

const ToggleRow = ({
  label,
  description,
  value,
  onToggle,
}: {
  label: string;
  description?: string;
  value: boolean;
  onToggle: (v: boolean) => void;
}) => {
  const { C, S } = useStyles();
  return (
    <Pressable
      onPress={() => onToggle(!value)}
      accessibilityRole="switch"
      accessibilityState={{ checked: value }}
      style={(s: any) => [S.toggleCard, s.hovered && S.toggleCardHover]}
    >
      <RNView style={{ flex: 1, minWidth: 0 }}>
        <Text style={S.toggleLabel}>{label}</Text>
        {!!description && <Text style={S.toggleDesc}>{description}</Text>}
      </RNView>
      <Switch
        value={value}
        onValueChange={onToggle}
        trackColor={{ false: C.border, true: C.accent + "80" }}
        thumbColor={value ? C.accent : "#fff"}
        {...(Platform.OS === "web"
          ? ({ activeThumbColor: C.accent } as any)
          : {})}
      />
    </Pressable>
  );
};

// ─── Main Dialog ──────────────────────────────────────────────────────────────

const AddEditOrganizationModal: React.FC<AddEditOrganizationModalProps> = ({
  visible,
  onClose,
  organization,
  onSave,
}) => {
  const { C, S } = useStyles();
  const { width, height } = useWindowDimensions();
  const showNav = width >= 900;
  const isMedium = width >= 640;

  const allStates: any[] = useSelector(
    (state: any) => state.portal.admin?.states?.states ?? [],
  );

  const [form, setForm] = useState<OrgFormData>(buildDefault(organization));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState<SectionKey>("basic");

  const scrollRef = useRef<ScrollView>(null);
  const offsets = useRef<Partial<Record<SectionKey, number>>>({});

  const isEdit = !!organization?._id;

  // Re-init each time the dialog opens
  useEffect(() => {
    if (visible) {
      setForm(buildDefault(organization));
      setErrors({});
      setSaveError(null);
      setActiveSection("basic");
    }
  }, [visible, organization]);

  // ── State helpers ─────────────────────────────────────────────────────────

  const clearErrors = useCallback((match: (key: string) => boolean) => {
    setErrors((prev) => {
      const keys = Object.keys(prev).filter(match);
      if (!keys.length) return prev;
      const next = { ...prev };
      keys.forEach((k) => delete next[k]);
      return next;
    });
  }, []);

  const set = useCallback(
    <K extends keyof OrgFormData>(key: K, value: OrgFormData[K]) => {
      setForm((prev) => ({ ...prev, [key]: value }));
      clearErrors((k) => k === key);
      setSaveError(null);
    },
    [clearErrors],
  );

  // Domains
  const addDomain = () => set("domains", [...form.domains, ""]);
  const removeDomain = (i: number) =>
    set(
      "domains",
      form.domains.filter((_, idx) => idx !== i),
    );
  const setDomain = (i: number, v: string) =>
    set(
      "domains",
      form.domains.map((d, idx) => (idx === i ? v : d)),
    );

  // Contacts
  const addContact = () =>
    set("contacts", [
      ...form.contacts,
      { address: "", email: "", phoneNumber: "" },
    ]);
  const removeContact = (i: number) => {
    set(
      "contacts",
      form.contacts.filter((_, idx) => idx !== i),
    );
    clearErrors((k) => k.startsWith("contact_"));
  };
  const setContactField = (i: number, field: keyof ContactRow, v: string) => {
    set(
      "contacts",
      form.contacts.map((c, idx) => (idx === i ? { ...c, [field]: v } : c)),
    );
    clearErrors((k) => k === `contact_${i}_${field}`);
  };

  // Sales reps
  const addSales = () => set("sales", [...form.sales, { name: "", email: "" }]);
  const removeSales = (i: number) => {
    const removed = form.sales[i];
    set(
      "sales",
      form.sales.filter((_, idx) => idx !== i),
    );
    if (removed && removed.email === form.pricingSalesEmail)
      set("pricingSalesEmail", null);
    clearErrors((k) => k.startsWith("sales_"));
  };
  const setSalesField = (i: number, field: keyof SalesRow, v: string) => {
    const prev = form.sales[i];
    set(
      "sales",
      form.sales.map((s, idx) => (idx === i ? { ...s, [field]: v } : s)),
    );
    // Keep the pricing rep linked if their email is edited
    if (
      field === "email" &&
      prev &&
      prev.email &&
      prev.email === form.pricingSalesEmail
    )
      set("pricingSalesEmail", v);
    clearErrors((k) => k === `sales_${i}_${field}`);
  };

  // States
  const toggleState = (id: string) =>
    set(
      "states",
      form.states.includes(id)
        ? form.states.filter((s) => s !== id)
        : [...form.states, id],
    );
  const selectAllStates = () =>
    set(
      "states",
      allStates.map((s: any) => s._id),
    );
  const clearStates = () => set("states", []);

  // Pricing
  const setPricingModel = (model: "default" | "sales") => {
    set("pricingModel", model);
    if (model === "default") set("pricingSalesEmail", null);
    else if (form.sales.length === 1 && !form.pricingSalesEmail)
      set("pricingSalesEmail", form.sales[0].email);
  };

  // ── Section navigation ────────────────────────────────────────────────────

  const registerSection = (key: SectionKey) => (e: LayoutChangeEvent) => {
    offsets.current[key] = e.nativeEvent.layout.y;
  };

  const scrollToSection = useCallback((key: SectionKey) => {
    const y = offsets.current[key] ?? 0;
    scrollRef.current?.scrollTo({ y: Math.max(0, y - 8), animated: true });
    setActiveSection(key);
  }, []);

  const handleScroll = (e: NativeSyntheticEvent<NativeScrollEvent>) => {
    const y = e.nativeEvent.contentOffset.y + 80;
    let current: SectionKey = "basic";
    for (const s of SECTIONS) {
      const off = offsets.current[s.key];
      if (off != null && off <= y) current = s.key;
    }
    if (current !== activeSection) setActiveSection(current);
  };

  const errorCountBySection = useMemo(() => {
    const counts: Partial<Record<SectionKey, number>> = {};
    Object.keys(errors).forEach((k) => {
      const s = sectionForError(k);
      counts[s] = (counts[s] ?? 0) + 1;
    });
    return counts;
  }, [errors]);
  const errorCount = Object.keys(errors).length;

  // ── Submit ────────────────────────────────────────────────────────────────

  const handleSubmit = async () => {
    const errs = validate(form);
    setErrors(errs);
    const keys = Object.keys(errs);
    if (keys.length) {
      scrollToSection(sectionForError(keys[0]));
      return;
    }
    setSubmitting(true);
    setSaveError(null);
    try {
      await onSave(toPayload(form, organization));
    } catch (e: any) {
      setSaveError(
        e?.message || e?.data?.message || "Couldn't save. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const selectedSalesRep = form.sales.find(
    (s) => !!s.email && s.email === form.pricingSalesEmail,
  );
  const validReps = form.sales.filter((s) => s.email.trim());

  // ── Render ────────────────────────────────────────────────────────────────

  const dialogSize = isMedium
    ? {
        width: "100%" as const,
        maxWidth: showNav ? 1040 : 760,
        height: Math.min(height - 48, 860),
      }
    : { width: "100%" as const, height: "100%" as const };

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      <RNView style={[S.backdrop, isMedium && S.backdropPadded]}>
        <KeyboardAvoidingView
          behavior={Platform.OS === "ios" ? "padding" : undefined}
          style={[S.dialog, !isMedium && S.dialogFull, dialogSize]}
        >
          {/* ── Header ── */}
          <RNView style={[S.header, !isMedium && S.headerMobile]}>
            <RNView style={{ flex: 1, minWidth: 0 }}>
              <Text style={S.headerTitle} numberOfLines={1}>
                {isEdit ? "Edit organisation" : "New organisation"}
              </Text>
              <Text style={S.headerSub} numberOfLines={1}>
                {isEdit
                  ? organization?.name
                  : "Set up a customer organisation, its pricing and what it can see."}
              </Text>
            </RNView>
            <IconButton icon="x" label="Close" onPress={onClose} />
          </RNView>

          {/* ── Body ── */}
          <RNView style={S.body}>
            {showNav && (
              <RNView style={S.nav}>
                {SECTIONS.map((s) => {
                  const on = activeSection === s.key;
                  const errs = errorCountBySection[s.key] ?? 0;
                  return (
                    <Pressable
                      key={s.key}
                      onPress={() => scrollToSection(s.key)}
                      accessibilityRole="tab"
                      accessibilityState={{ selected: on }}
                      style={(st: any) => [
                        S.navItem,
                        st.hovered && S.navItemHover,
                        on && S.navItemActive,
                      ]}
                    >
                      <FeatherIcon
                        name={s.icon}
                        size={15}
                        color={on ? C.accent : C.textMuted}
                      />
                      <Text style={[S.navText, on && S.navTextActive]}>
                        {s.label}
                      </Text>
                      {errs > 0 && (
                        <RNView style={S.navBadge}>
                          <Text style={S.navBadgeText}>{errs}</Text>
                        </RNView>
                      )}
                    </Pressable>
                  );
                })}
              </RNView>
            )}

            <ScrollView
              ref={scrollRef}
              style={{ flex: 1 }}
              contentContainerStyle={[
                S.content,
                !isMedium && { paddingHorizontal: 16 },
              ]}
              showsVerticalScrollIndicator
              keyboardShouldPersistTaps="handled"
              onScroll={handleScroll}
              scrollEventThrottle={32}
            >
              {/* ── General ── */}
              <Section
                title="General"
                description="Name, codes and how orders are numbered."
                icon="briefcase"
                onLayout={registerSection("basic")}
              >
                <RNView
                  style={isMedium ? { flexDirection: "row", gap: 20 } : null}
                >
                  <RNView
                    style={[S.logoBox, !isMedium && { marginBottom: 14 }]}
                  >
                    {organization?.logoUrl ? (
                      <Image
                        source={{ uri: organization.logoUrl }}
                        style={S.logoImg}
                      />
                    ) : form.name.trim() ? (
                      <Text style={S.logoInitials}>
                        {getInitials(form.name)}
                      </Text>
                    ) : (
                      <FeatherIcon
                        name="briefcase"
                        size={24}
                        color={C.accent}
                      />
                    )}
                  </RNView>

                  <RNView style={{ flex: 1, minWidth: 0, gap: 14 }}>
                    <Field
                      label="Organisation name"
                      required
                      error={errors.name}
                    >
                      <Input
                        value={form.name}
                        onChangeText={(v) => set("name", v)}
                        placeholder="ACME Pty Ltd"
                        error={errors.name}
                      />
                    </Field>
                    <Cols stack={!isMedium}>
                      <Field
                        label="Card code"
                        required
                        error={errors.cardCode}
                        hint="Separate multiple codes with commas"
                      >
                        <Input
                          value={form.cardCode}
                          onChangeText={(v) => set("cardCode", v)}
                          placeholder="C001"
                          autoCapitalize="characters"
                          error={errors.cardCode}
                        />
                      </Field>
                      <Field
                        label="Order prefix"
                        required
                        error={errors.prefix}
                        hint="Up to 5 characters"
                      >
                        <Input
                          value={form.prefix}
                          onChangeText={(v) => set("prefix", v)}
                          placeholder="ACM"
                          autoCapitalize="characters"
                          maxLength={5}
                          error={errors.prefix}
                        />
                      </Field>
                    </Cols>
                    <Field label="Description">
                      <Input
                        value={form.description}
                        onChangeText={(v) => set("description", v)}
                        placeholder="A short note about this organisation"
                        multiline
                        lines={3}
                      />
                    </Field>
                  </RNView>
                </RNView>
              </Section>

              {/* ── Domains ── */}
              <Section
                title="Domains"
                description="Email domains that belong to this organisation."
                icon="globe"
                onLayout={registerSection("domains")}
                action={
                  form.domains.length > 0 ? (
                    <SmallButton label="Add" icon="plus" onPress={addDomain} />
                  ) : null
                }
              >
                {form.domains.length === 0 ? (
                  <EmptyBox
                    text="No domains added"
                    actionLabel="Add domain"
                    onAction={addDomain}
                  />
                ) : (
                  <RNView style={{ gap: 8 }}>
                    {form.domains.map((domain, i) => (
                      <RNView key={i} style={S.inlineRow}>
                        <RNView style={{ flex: 1 }}>
                          <Input
                            value={domain}
                            onChangeText={(v) => setDomain(i, v)}
                            placeholder="example.com"
                            autoCapitalize="none"
                            icon="at-sign"
                          />
                        </RNView>
                        <IconButton
                          icon="trash-2"
                          label="Remove domain"
                          danger
                          onPress={() => removeDomain(i)}
                        />
                      </RNView>
                    ))}
                  </RNView>
                )}
              </Section>

              {/* ── Contacts ── */}
              <Section
                title="Contacts"
                description="Addresses and people to reach at this organisation."
                icon="phone"
                onLayout={registerSection("contacts")}
                action={
                  form.contacts.length > 0 ? (
                    <SmallButton label="Add" icon="plus" onPress={addContact} />
                  ) : null
                }
              >
                {form.contacts.length === 0 ? (
                  <EmptyBox
                    text="No contacts added"
                    actionLabel="Add contact"
                    onAction={addContact}
                  />
                ) : (
                  <RNView style={{ gap: 12 }}>
                    {form.contacts.map((contact, i) => (
                      <RNView key={i} style={S.subCard}>
                        <RNView style={S.subCardHead}>
                          <Text style={S.subCardTitle}>Contact {i + 1}</Text>
                          <IconButton
                            icon="trash-2"
                            label={`Remove contact ${i + 1}`}
                            danger
                            onPress={() => removeContact(i)}
                          />
                        </RNView>
                        <RNView style={{ gap: 12 }}>
                          <Field
                            label="Address"
                            required
                            error={errors[`contact_${i}_address`]}
                          >
                            <Input
                              value={contact.address}
                              onChangeText={(v) =>
                                setContactField(i, "address", v)
                              }
                              placeholder="123 Main St, Sydney"
                              error={errors[`contact_${i}_address`]}
                            />
                          </Field>
                          <Cols stack={!isMedium}>
                            <Field
                              label="Email"
                              required
                              error={errors[`contact_${i}_email`]}
                            >
                              <Input
                                value={contact.email}
                                onChangeText={(v) =>
                                  setContactField(i, "email", v)
                                }
                                placeholder="hello@acme.com"
                                keyboardType="email-address"
                                autoCapitalize="none"
                                icon="mail"
                                error={errors[`contact_${i}_email`]}
                              />
                            </Field>
                            <Field
                              label="Phone"
                              required
                              error={errors[`contact_${i}_phoneNumber`]}
                            >
                              <Input
                                value={contact.phoneNumber}
                                onChangeText={(v) =>
                                  setContactField(i, "phoneNumber", v)
                                }
                                placeholder="+61 400 000 000"
                                keyboardType="phone-pad"
                                icon="phone"
                                error={errors[`contact_${i}_phoneNumber`]}
                              />
                            </Field>
                          </Cols>
                        </RNView>
                      </RNView>
                    ))}
                  </RNView>
                )}
              </Section>

              {/* ── Sales reps ── */}
              <Section
                title="Sales representatives"
                description="Reps assigned to this organisation can open it and place orders."
                icon="users"
                onLayout={registerSection("sales")}
                action={
                  form.sales.length > 0 ? (
                    <SmallButton label="Add" icon="plus" onPress={addSales} />
                  ) : null
                }
              >
                {form.sales.length === 0 ? (
                  <EmptyBox
                    text="No sales reps assigned"
                    actionLabel="Add rep"
                    onAction={addSales}
                  />
                ) : (
                  <RNView style={{ gap: 10 }}>
                    {form.sales.map((rep, i) => {
                      const isPricingRep =
                        !!rep.email && rep.email === form.pricingSalesEmail;
                      return (
                        <RNView key={i} style={S.subCard}>
                          <RNView style={S.subCardHead}>
                            <RNView style={S.subCardTitleRow}>
                              <Text style={S.subCardTitle}>Rep {i + 1}</Text>
                              {isPricingRep && (
                                <RNView style={S.tag}>
                                  <Text style={S.tagText}>Pricing rep</Text>
                                </RNView>
                              )}
                            </RNView>
                            <IconButton
                              icon="trash-2"
                              label={`Remove rep ${i + 1}`}
                              danger
                              onPress={() => removeSales(i)}
                            />
                          </RNView>
                          <Cols stack={!isMedium}>
                            <Field
                              label="Name"
                              required
                              error={errors[`sales_${i}_name`]}
                            >
                              <Input
                                value={rep.name}
                                onChangeText={(v) =>
                                  setSalesField(i, "name", v)
                                }
                                placeholder="Jane Smith"
                                icon="user"
                                error={errors[`sales_${i}_name`]}
                              />
                            </Field>
                            <Field
                              label="Email"
                              required
                              error={errors[`sales_${i}_email`]}
                            >
                              <Input
                                value={rep.email}
                                onChangeText={(v) =>
                                  setSalesField(i, "email", v)
                                }
                                placeholder="jane@acme.com"
                                keyboardType="email-address"
                                autoCapitalize="none"
                                icon="mail"
                                error={errors[`sales_${i}_email`]}
                              />
                            </Field>
                          </Cols>
                        </RNView>
                      );
                    })}
                  </RNView>
                )}
              </Section>

              {/* ── Pricing ── */}
              <Section
                title="Pricing"
                description="Whether prices are shown, and which price list is used."
                icon="dollar-sign"
                onLayout={registerSection("pricing")}
              >
                <RNView style={{ gap: 14 }}>
                  <ToggleRow
                    label="Show pricing"
                    description="Let this organisation see prices"
                    value={form.pricingEnabled}
                    onToggle={(v) => set("pricingEnabled", v)}
                  />

                  <Field label="Price list">
                    <RNView style={S.segment}>
                      {(
                        [
                          { value: "default", label: "Default prices" },
                          { value: "sales", label: "Sales rep prices" },
                        ] as const
                      ).map((opt) => {
                        const on = form.pricingModel === opt.value;
                        return (
                          <Pressable
                            key={opt.value}
                            onPress={() => setPricingModel(opt.value)}
                            accessibilityRole="radio"
                            accessibilityState={{ checked: on }}
                            style={[S.segmentBtn, on && S.segmentBtnActive]}
                          >
                            <Text
                              style={[S.segmentText, on && S.segmentTextActive]}
                            >
                              {opt.label}
                            </Text>
                          </Pressable>
                        );
                      })}
                    </RNView>
                  </Field>

                  {form.pricingModel === "sales" &&
                    (validReps.length === 0 ? (
                      <RNView style={S.warnBanner}>
                        <FeatherIcon
                          name="alert-triangle"
                          size={14}
                          color={C.warning}
                        />
                        <Text style={S.warnText}>
                          Add a sales rep with an email above to use their
                          prices.
                        </Text>
                      </RNView>
                    ) : (
                      <Field
                        label="Use prices from"
                        required
                        error={errors.pricingSalesEmail}
                      >
                        <RNView
                          style={[
                            S.radioList,
                            !!errors.pricingSalesEmail && {
                              borderColor: C.danger,
                            },
                          ]}
                        >
                          {validReps.map((rep) => {
                            const on = rep.email === form.pricingSalesEmail;
                            return (
                              <Pressable
                                key={rep.email}
                                onPress={() =>
                                  set("pricingSalesEmail", rep.email)
                                }
                                accessibilityRole="radio"
                                accessibilityState={{ checked: on }}
                                style={(s: any) => [
                                  S.radioRow,
                                  s.hovered && S.radioRowHover,
                                  on && S.radioRowActive,
                                ]}
                              >
                                <RNView style={[S.radio, on && S.radioOn]}>
                                  {on && <RNView style={S.radioDot} />}
                                </RNView>
                                <RNView style={{ flex: 1, minWidth: 0 }}>
                                  <Text style={S.radioLabel} numberOfLines={1}>
                                    {rep.name || "Unnamed rep"}
                                  </Text>
                                  <Text style={S.radioSub} numberOfLines={1}>
                                    {rep.email}
                                  </Text>
                                </RNView>
                              </Pressable>
                            );
                          })}
                        </RNView>
                      </Field>
                    ))}

                  <RNView style={S.summary}>
                    <FeatherIcon name="info" size={13} color={C.textMuted} />
                    <Text style={S.summaryText}>
                      {!form.pricingEnabled
                        ? "Prices are hidden from this organisation."
                        : form.pricingModel === "sales"
                          ? selectedSalesRep
                            ? `Shows ${selectedSalesRep.name || selectedSalesRep.email}'s prices.`
                            : "Shows a sales rep's prices — pick one above."
                          : "Shows default prices."}
                    </Text>
                  </RNView>
                </RNView>
              </Section>

              {/* ── States ── */}
              <Section
                title="States"
                description={
                  allStates.length
                    ? `${form.states.length} of ${allStates.length} selected`
                    : "Where this organisation operates."
                }
                icon="map-pin"
                onLayout={registerSection("states")}
                action={
                  allStates.length > 0 ? (
                    <RNView style={{ flexDirection: "row", gap: 6 }}>
                      <SmallButton
                        label="All"
                        tone="neutral"
                        onPress={selectAllStates}
                      />
                      <SmallButton
                        label="None"
                        tone="neutral"
                        onPress={clearStates}
                      />
                    </RNView>
                  ) : null
                }
              >
                {!!errors.states && (
                  <Text style={[S.errorText, { marginBottom: 10 }]}>
                    {errors.states}
                  </Text>
                )}
                {allStates.length === 0 ? (
                  <RNView style={S.loadingRow}>
                    <ActivityIndicator size="small" color={C.accent} />
                    <Text style={S.emptyText}>Loading states…</Text>
                  </RNView>
                ) : (
                  <RNView style={S.chips}>
                    {allStates.map((st: any) => {
                      const on = form.states.includes(st._id);
                      return (
                        <Pressable
                          key={st._id}
                          onPress={() => toggleState(st._id)}
                          accessibilityRole="checkbox"
                          accessibilityState={{ checked: on }}
                          style={(s: any) => [
                            S.chip,
                            s.hovered && S.chipHover,
                            on && S.chipActive,
                          ]}
                        >
                          {on && (
                            <FeatherIcon
                              name="check"
                              size={12}
                              color={C.accent}
                            />
                          )}
                          <Text style={[S.chipText, on && S.chipTextActive]}>
                            {st.name}
                          </Text>
                        </Pressable>
                      );
                    })}
                  </RNView>
                )}
              </Section>

              {/* ── Visibility ── */}
              <Section
                title="Visibility"
                description="What members of this organisation can see."
                icon="eye"
                onLayout={registerSection("visibility")}
                last
              >
                <RNView style={S.toggleGrid}>
                  {(
                    [
                      {
                        key: "showAvailableStock",
                        label: "Available stock",
                        description: "Stock that can be ordered now",
                      },
                      {
                        key: "showOnOrderStock",
                        label: "On-order stock",
                        description: "Stock that is on order",
                      },
                      {
                        key: "showStockOnTransit",
                        label: "In-transit stock",
                        description: "Stock currently in transit",
                      },
                      {
                        key: "showGrowers",
                        label: "Growers",
                        description: "Grower information",
                      },
                    ] as {
                      key: keyof OrgFormData;
                      label: string;
                      description: string;
                    }[]
                  ).map(({ key, label, description }) => (
                    <RNView
                      key={key}
                      style={isMedium ? S.toggleGridItem : undefined}
                    >
                      <ToggleRow
                        label={label}
                        description={description}
                        value={!!form[key]}
                        onToggle={(v) => set(key, v as any)}
                      />
                    </RNView>
                  ))}
                </RNView>
              </Section>
            </ScrollView>
          </RNView>

          {/* ── Footer ── */}
          <RNView style={[S.footer, !isMedium && S.footerMobile]}>
            <RNView style={S.footerStatus}>
              {saveError ? (
                <>
                  <FeatherIcon name="alert-circle" size={14} color={C.danger} />
                  <Text style={S.footerError} numberOfLines={2}>
                    {saveError}
                  </Text>
                </>
              ) : errorCount > 0 ? (
                <>
                  <FeatherIcon name="alert-circle" size={14} color={C.danger} />
                  <Text style={S.footerError} numberOfLines={1}>
                    {errorCount === 1
                      ? "1 field needs attention"
                      : `${errorCount} fields need attention`}
                  </Text>
                </>
              ) : isMedium ? (
                <Text style={S.footerHint}>
                  Fields marked <Text style={{ color: C.danger }}>*</Text> are
                  required
                </Text>
              ) : null}
            </RNView>
            <Pressable
              onPress={onClose}
              disabled={submitting}
              accessibilityRole="button"
              style={(s: any) => [
                S.btnSecondary,
                (s.hovered || s.pressed) && { backgroundColor: C.surfaceAlt },
              ]}
            >
              <Text style={S.btnSecondaryText}>Cancel</Text>
            </Pressable>
            <Pressable
              onPress={handleSubmit}
              disabled={submitting}
              accessibilityRole="button"
              style={(s: any) => [
                S.btnPrimary,
                (s.hovered || s.pressed) && { opacity: 0.9 },
                submitting && { opacity: 0.6 },
              ]}
            >
              {submitting ? (
                <ActivityIndicator size="small" color="#fff" />
              ) : (
                <Text style={S.btnPrimaryText}>
                  {isEdit ? "Save changes" : "Create organisation"}
                </Text>
              )}
            </Pressable>
          </RNView>
        </KeyboardAvoidingView>
      </RNView>
    </Modal>
  );
};

export default AddEditOrganizationModal;

// ─── Styles ───────────────────────────────────────────────────────────────────

const createStyles = (C: Tokens) =>
  StyleSheet.create({
    // Shell
    backdrop: {
      flex: 1,
      backgroundColor: C.overlay,
      justifyContent: "center",
      alignItems: "center",
    },
    backdropPadded: { padding: 24 },
    dialog: {
      backgroundColor: C.surface,
      borderRadius: 16,
      overflow: "hidden",
      borderWidth: 1,
      borderColor: C.border,
      shadowColor: "#000",
      shadowOpacity: 0.2,
      shadowRadius: 32,
      shadowOffset: { width: 0, height: 16 },
      elevation: 12,
    },
    dialogFull: { borderRadius: 0, borderWidth: 0 },

    header: {
      flexDirection: "row",
      alignItems: "center",
      gap: 12,
      paddingHorizontal: 24,
      paddingVertical: 16,
      borderBottomWidth: 1,
      borderBottomColor: C.border,
    },
    headerMobile: {
      paddingHorizontal: 16,
      paddingTop: Platform.OS === "ios" ? 52 : 16,
    },
    headerTitle: { fontSize: 18, fontWeight: "800", color: C.text },
    headerSub: { fontSize: 13, color: C.textMuted, marginTop: 2 },

    body: { flex: 1, flexDirection: "row", minHeight: 0 },

    // Side nav
    nav: {
      width: 210,
      paddingVertical: 16,
      paddingHorizontal: 12,
      gap: 2,
      borderRightWidth: 1,
      borderRightColor: C.border,
      backgroundColor: C.surfaceAlt,
    },
    navItem: {
      flexDirection: "row",
      alignItems: "center",
      gap: 10,
      paddingVertical: 9,
      paddingHorizontal: 12,
      borderRadius: 8,
    },
    navItemHover: { backgroundColor: C.border + "66" },
    navItemActive: { backgroundColor: C.accent + "14" },
    navText: { flex: 1, fontSize: 13, fontWeight: "600", color: C.textMid },
    navTextActive: { color: C.accent, fontWeight: "700" },
    navBadge: {
      minWidth: 18,
      height: 18,
      borderRadius: 9,
      paddingHorizontal: 5,
      backgroundColor: C.danger,
      justifyContent: "center",
      alignItems: "center",
    },
    navBadgeText: { fontSize: 10, fontWeight: "800", color: "#fff" },

    content: { paddingHorizontal: 28, paddingBottom: 24 },

    // Sections
    section: {
      paddingVertical: 24,
      borderBottomWidth: 1,
      borderBottomColor: C.borderLight,
    },
    sectionLast: { borderBottomWidth: 0 },
    sectionHead: {
      flexDirection: "row",
      alignItems: "center",
      gap: 12,
      marginBottom: 16,
    },
    sectionIcon: {
      width: 32,
      height: 32,
      borderRadius: 9,
      backgroundColor: C.accent + "14",
      justifyContent: "center",
      alignItems: "center",
    },
    sectionTitle: { fontSize: 15, fontWeight: "800", color: C.text },
    sectionDesc: { fontSize: 12, color: C.textMuted, marginTop: 2 },

    // Fields
    field: { gap: 6 },
    label: { fontSize: 12, fontWeight: "700", color: C.textMid },
    inputWrap: {
      flexDirection: "row",
      alignItems: "center",
      gap: 8,
      borderWidth: 1.5,
      borderColor: C.border,
      borderRadius: 10,
      backgroundColor: C.surface,
      paddingHorizontal: 12,
      height: 42,
    },
    inputWrapFocused: { borderColor: C.accent },
    inputWrapError: { borderColor: C.danger },
    input: { flex: 1, fontSize: 14, color: C.text, paddingVertical: 0 },
    errorText: { fontSize: 12, color: C.danger, fontWeight: "600" },
    hintText: { fontSize: 12, color: C.textMuted },

    // Logo
    logoBox: {
      width: 72,
      height: 72,
      borderRadius: 18,
      backgroundColor: C.accent + "14",
      justifyContent: "center",
      alignItems: "center",
      overflow: "hidden",
    },
    logoImg: { width: 72, height: 72 },
    logoInitials: { fontSize: 24, fontWeight: "800", color: C.accent },

    // Buttons
    smallBtn: {
      flexDirection: "row",
      alignItems: "center",
      gap: 5,
      height: 30,
      paddingHorizontal: 12,
      borderRadius: 8,
      backgroundColor: C.accent + "12",
    },
    smallBtnNeutral: { backgroundColor: C.surfaceAlt },
    smallBtnText: { fontSize: 12, fontWeight: "700" },

    // Lists
    inlineRow: { flexDirection: "row", alignItems: "center", gap: 6 },
    emptyBox: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
      borderWidth: 1.5,
      borderStyle: "dashed",
      borderColor: C.border,
      borderRadius: 12,
      paddingVertical: 14,
      paddingHorizontal: 16,
    },
    emptyText: { fontSize: 13, color: C.textMuted },
    loadingRow: { flexDirection: "row", alignItems: "center", gap: 10 },
    subCard: {
      borderWidth: 1,
      borderColor: C.border,
      borderRadius: 12,
      padding: 14,
      paddingTop: 8,
      backgroundColor: C.surfaceAlt,
    },
    subCardHead: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: 6,
    },
    subCardTitleRow: { flexDirection: "row", alignItems: "center", gap: 8 },
    subCardTitle: { fontSize: 13, fontWeight: "700", color: C.textMid },
    tag: {
      paddingHorizontal: 8,
      paddingVertical: 2,
      borderRadius: 999,
      backgroundColor: C.accent + "14",
    },
    tagText: { fontSize: 11, fontWeight: "700", color: C.accent },

    // Pricing
    segment: {
      flexDirection: "row",
      backgroundColor: C.surfaceAlt,
      borderRadius: 10,
      padding: 3,
      alignSelf: "flex-start",
    },
    segmentBtn: {
      paddingVertical: 8,
      paddingHorizontal: 14,
      borderRadius: 8,
    },
    segmentBtnActive: {
      backgroundColor: C.surface,
      shadowColor: "#000",
      shadowOpacity: 0.08,
      shadowRadius: 3,
      shadowOffset: { width: 0, height: 1 },
      elevation: 1,
    },
    segmentText: { fontSize: 13, fontWeight: "600", color: C.textMuted },
    segmentTextActive: { color: C.text, fontWeight: "700" },
    radioList: {
      borderWidth: 1,
      borderColor: C.border,
      borderRadius: 12,
      overflow: "hidden",
    },
    radioRow: {
      flexDirection: "row",
      alignItems: "center",
      gap: 12,
      paddingVertical: 10,
      paddingHorizontal: 14,
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: C.border,
    },
    radioRowHover: { backgroundColor: C.surfaceAlt },
    radioRowActive: { backgroundColor: C.accent + "0D" },
    radio: {
      width: 18,
      height: 18,
      borderRadius: 9,
      borderWidth: 2,
      borderColor: C.border,
      justifyContent: "center",
      alignItems: "center",
    },
    radioOn: { borderColor: C.accent },
    radioDot: {
      width: 8,
      height: 8,
      borderRadius: 4,
      backgroundColor: C.accent,
    },
    radioLabel: { fontSize: 14, fontWeight: "600", color: C.text },
    radioSub: { fontSize: 12, color: C.textMuted, marginTop: 1 },
    warnBanner: {
      flexDirection: "row",
      alignItems: "center",
      gap: 8,
      backgroundColor: C.warningBg,
      padding: 12,
      borderRadius: 10,
    },
    warnText: { fontSize: 12, color: C.warning, flex: 1, fontWeight: "600" },
    summary: {
      flexDirection: "row",
      alignItems: "center",
      gap: 8,
      backgroundColor: C.surfaceAlt,
      borderRadius: 10,
      paddingVertical: 10,
      paddingHorizontal: 12,
    },
    summaryText: { fontSize: 12, color: C.textMid, flex: 1 },

    // States
    chips: { flexDirection: "row", flexWrap: "wrap", gap: 8 },
    chip: {
      flexDirection: "row",
      alignItems: "center",
      gap: 5,
      paddingVertical: 7,
      paddingHorizontal: 12,
      borderRadius: 999,
      borderWidth: 1.5,
      borderColor: C.border,
      backgroundColor: C.surface,
    },
    chipHover: { borderColor: C.accent + "66" },
    chipActive: { borderColor: C.accent, backgroundColor: C.accent + "12" },
    chipText: { fontSize: 13, fontWeight: "600", color: C.textMid },
    chipTextActive: { color: C.accent, fontWeight: "700" },

    // Toggles
    toggleGrid: { flexDirection: "row", flexWrap: "wrap", gap: 10 },
    toggleGridItem: { width: "48.8%", flexGrow: 1 },
    toggleCard: {
      flexDirection: "row",
      alignItems: "center",
      gap: 12,
      borderWidth: 1,
      borderColor: C.border,
      borderRadius: 12,
      paddingVertical: 12,
      paddingHorizontal: 14,
      backgroundColor: C.surface,
      width: "100%",
    },
    toggleCardHover: { backgroundColor: C.surfaceAlt },
    toggleLabel: { fontSize: 14, fontWeight: "700", color: C.text },
    toggleDesc: { fontSize: 12, color: C.textMuted, marginTop: 2 },

    // Footer
    footer: {
      flexDirection: "row",
      alignItems: "center",
      gap: 10,
      paddingHorizontal: 24,
      paddingVertical: 14,
      borderTopWidth: 1,
      borderTopColor: C.border,
      backgroundColor: C.surface,
    },
    footerMobile: {
      paddingHorizontal: 16,
      paddingBottom: Platform.OS === "ios" ? 28 : 14,
    },
    footerStatus: {
      flex: 1,
      minWidth: 0,
      flexDirection: "row",
      alignItems: "center",
      gap: 6,
    },
    footerError: { fontSize: 13, color: C.danger, fontWeight: "600", flex: 1 },
    footerHint: { fontSize: 12, color: C.textMuted },
    btnSecondary: {
      height: 40,
      paddingHorizontal: 16,
      borderRadius: 10,
      borderWidth: 1,
      borderColor: C.border,
      justifyContent: "center",
      alignItems: "center",
    },
    btnSecondaryText: { fontSize: 14, fontWeight: "700", color: C.textMid },
    btnPrimary: {
      height: 40,
      minWidth: 120,
      paddingHorizontal: 18,
      borderRadius: 10,
      backgroundColor: C.accent,
      justifyContent: "center",
      alignItems: "center",
    },
    btnPrimaryText: { fontSize: 14, fontWeight: "800", color: "#fff" },
  });
